import {
  getMemberSelfBalance,
  memberSelfPaymentReceiptDownloadUrl,
  memberSelfPaymentReceiptUrl,
  type MemberSelfBalance,
} from '@urbanik/api-client';
import {
  CanonicalMetricCard,
  CanonicalMetricGrid,
  StatePanel,
  PageSizeSelector,
  Pagination,
  ResultsCounter,
  StatusBadge,
  canonicalStates,
  formatUsd,
} from '@urbanik/ui';
import {
  CircleCheck,
  Clock3,
  IdCard,
  CircleDollarSign,
  CreditCard,
  Eye,
  ListChecks,
  ReceiptText,
  UserRound,
  ShieldCheck,
  WalletCards,
} from 'lucide-react';
const periodMonths = [
  'ENE',
  'FEB',
  'MAR',
  'ABR',
  'MAY',
  'JUN',
  'JUL',
  'AGO',
  'SEP',
  'OCT',
  'NOV',
  'DIC',
] as const;
function formatPeriod(period: string) {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period);
  return match ? `${periodMonths[Number(match[2]) - 1]}-${match[1]}` : period;
}
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
import { PaymentApplicationDetailModal } from './PaymentApplicationDetailModal';
import { openProtectedDocument } from './protected-document';
import './member-self-balance.css';
export function MemberSelfBalanceView() {
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading'),
    [data, setData] = useState<MemberSelfBalance | null>(null),
    [page, setPage] = useState(1),
    [yearsPerPage, setYearsPerPage] = useState(3),
    [expandedYear, setExpandedYear] = useState<string | null>(null),
    [documentError, setDocumentError] = useState(''),
    [loadError, setLoadError] = useState(''),
    [selectedPayment, setSelectedPayment] = useState<
      | MemberSelfBalance['account']['payments'][number]
      | NonNullable<MemberSelfBalance['latestPayment']>
      | null
    >(null);
  useEffect(() => {
    const controller = new AbortController();
    getMemberSelfBalance(apiBase, controller.signal)
      .then((value) => {
        setData(value);
        setState('success');
        const paymentId = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get(
          'payment',
        );
        if (paymentId)
          setSelectedPayment(
            value.account.payments.find((payment) => payment.id === paymentId) ?? null,
          );
      })
      .catch((failure) => {
        if (controller.signal.aborted) return;
        setLoadError(
          failure instanceof Error
            ? failure.message
            : 'No se pudo consultar la cuenta vinculada a tu usuario.',
        );
        setState('error');
      });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (!data) return;
    const openPayment = (event: Event) => {
      const paymentId = (event as CustomEvent<string>).detail;
      const current = data.account.payments.find((payment) => payment.id === paymentId);
      if (current) {
        setSelectedPayment(current);
        return;
      }
      void getMemberSelfBalance(apiBase)
        .then((value) => {
          setData(value);
          setSelectedPayment(
            value.account.payments.find((payment) => payment.id === paymentId) ?? null,
          );
        })
        .catch(() => undefined);
    };
    const refreshApprovedPayment = () => {
      void getMemberSelfBalance(apiBase)
        .then((value) => setData(value))
        .catch(() => undefined);
    };
    window.addEventListener('urbanik:open-payment-detail', openPayment);
    window.addEventListener('urbanik:member-payment-updated', refreshApprovedPayment);
    return () => {
      window.removeEventListener('urbanik:open-payment-detail', openPayment);
      window.removeEventListener('urbanik:member-payment-updated', refreshApprovedPayment);
    };
  }, [data]);
  if (state !== 'success' || !data) {
    const definition = canonicalStates.find((item) => item.kind === state)!;
    return (
      <StatePanel
        state={
          state === 'error'
            ? {
                ...definition,
                title: 'No pudimos abrir tu estado de cuenta',
                message:
                  loadError ||
                  'Tu sesión está activa, pero no fue posible consultar la información de tu entidad.',
                actionLabel: 'Intentar nuevamente',
              }
            : definition
        }
        {...(state === 'error' ? { onAction: () => window.location.reload() } : {})}
      />
    );
  }
  const obligationsByYear = Object.entries(
    data.items.reduce<Record<string, MemberSelfBalance['items']>>((groups, item) => {
      const year = item.period.slice(0, 4) || 'Sin año';
      (groups[year] ??= []).push(item);
      return groups;
    }, {}),
  ).sort(([yearA], [yearB]) => yearB.localeCompare(yearA));
  const totalPages = Math.max(1, Math.ceil(obligationsByYear.length / yearsPerPage));
  const visibleYears = obligationsByYear.slice((page - 1) * yearsPerPage, page * yearsPerPage);
  const latestPayment = data.latestPayment;
  const paidCount =
    latestPayment?.applications.filter((item) => item.status === 'paid').length ?? 0;
  const partialCount =
    latestPayment?.applications.filter((item) => item.status === 'partially_paid').length ?? 0;
  const showLatestPayment = () => {
    const year = latestPayment?.applications[0]?.period.slice(0, 4);
    if (!year) return;
    const index = obligationsByYear.findIndex(([value]) => value === year);
    if (index >= 0) setPage(Math.floor(index / yearsPerPage) + 1);
    setExpandedYear(year);
    setTimeout(
      () =>
        document.getElementById('member-paid-obligations')?.scrollIntoView({ behavior: 'smooth' }),
      0,
    );
  };
  return (
    <div className="member-self">
      <header className="member-self__heading">
        <div>
          <span>PORTAL DEL SOCIO</span>
          <h1>Estado De Cuenta</h1>
          <p className="member-self__identity">
            <span className="member-self__member-name">
              <UserRound aria-hidden="true" />
              {data.member.displayName}
            </span>
            <span className="member-self__national-id">
              <IdCard aria-hidden="true" />
              <span>
                <strong aria-label={`Cédula ${data.member.nationalId}`}>
                  {data.member.nationalId}
                </strong>
              </span>
            </span>
          </p>
        </div>
        <StatusBadge tone="success">
          <ShieldCheck aria-hidden="true" /> Consulta privada
        </StatusBadge>
      </header>
      {latestPayment ? (
        <aside className="member-self__payment-notice" role="status">
          <CircleCheck aria-hidden="true" />
          <div>
            <strong>Pago aplicado correctamente</strong>
            <p>
              Con el comprobante {latestPayment.sourceReference} se pagaron {paidCount} cuotas
              completas{partialCount ? ` y se registró ${partialCount} abono parcial` : ''}. Total
              aplicado: {formatUsd(latestPayment.appliedAmount)}.
            </p>
          </div>
          <div className="member-self__payment-notice-actions">
            <button
              type="button"
              className="ui-button ui-button--secondary"
              onClick={() => setSelectedPayment(latestPayment)}
            >
              Ver detalle del pago
            </button>
            <button
              type="button"
              className="ui-button ui-button--secondary"
              onClick={showLatestPayment}
            >
              Consultar cuotas pagadas
            </button>
            <button
              type="button"
              className="ui-button ui-button--primary"
              onClick={() => {
                setDocumentError('');
                void openProtectedDocument(
                  memberSelfPaymentReceiptDownloadUrl(apiBase, latestPayment.id),
                  'download',
                  `${latestPayment.receiptNumber ?? 'recibo'}.png`,
                ).catch((failure) =>
                  setDocumentError(
                    failure instanceof Error ? failure.message : 'No se pudo descargar el recibo.',
                  ),
                );
              }}
            >
              Descargar recibo oficial
            </button>
          </div>
        </aside>
      ) : null}
      {documentError ? (
        <div className="ui-alert" role="alert">
          {documentError}
        </div>
      ) : null}
      <CanonicalMetricGrid>
        <CanonicalMetricCard
          label="Obligaciones totales"
          value={data.account.totalObligations}
          icon={<ListChecks aria-hidden="true" />}
          tone="info"
          detail={`${formatUsd(data.account.obligations)} en cargos confirmados`}
        />
        <CanonicalMetricCard
          label="Obligaciones pagadas"
          value={data.account.paidObligations}
          icon={<CircleDollarSign aria-hidden="true" />}
          tone="success"
          detail={`${formatUsd(data.account.appliedPayments)} aplicado`}
        />
        <CanonicalMetricCard
          label="Obligaciones pendientes"
          value={data.account.pendingObligations}
          icon={<WalletCards aria-hidden="true" />}
          tone="warning"
          detail="Incluye obligaciones parcialmente pagadas"
        />
        <CanonicalMetricCard
          label="Saldo pendiente"
          value={formatUsd(data.account.outstanding)}
          icon={<CreditCard aria-hidden="true" />}
          tone="warning"
          detail="Obligaciones menos pagos aplicados"
        />
      </CanonicalMetricGrid>
      <section className="member-self__statement member-self__aliquots">
        <header>
          <div>
            <span>DESGLOSE FINANCIERO</span>
            <h2>Estado por alícuota</h2>
          </div>
          <small>{data.account.aliquots.length} alícuotas vinculadas</small>
        </header>
        <div className="member-self__table">
          <table>
            <thead>
              <tr>
                <th>Alícuota</th>
                <th>Total obligaciones</th>
                <th>Pagos aplicados</th>
                <th>Saldo pendiente</th>
                <th>Crédito a favor</th>
              </tr>
            </thead>
            <tbody>
              {data.account.aliquots.map((aliquot) => (
                <tr key={aliquot.slotNumber}>
                  <td>
                    <strong className="member-self__aliquot-number">{aliquot.slotNumber}</strong>
                  </td>
                  <td>{formatUsd(aliquot.obligations)}</td>
                  <td className="member-self__money--paid">{formatUsd(aliquot.appliedPayments)}</td>
                  <td className="member-self__money--pending">{formatUsd(aliquot.outstanding)}</td>
                  <td>{formatUsd(aliquot.credit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section
        id="member-paid-obligations"
        className="member-self__statement member-self__obligations"
      >
        <header>
          <div>
            <span>DETALLE</span>
            <h2>Obligaciones por período</h2>
          </div>
          <small>Valores expresados en dólares de los Estados Unidos.</small>
        </header>
        {data.items.length ? (
          <div className="member-self__table">
            <table>
              <thead>
                <tr>
                  <th>Alícuota</th>
                  <th>Período</th>
                  <th>Concepto</th>
                  <th>Estado</th>
                  <th>Valor</th>
                  <th>Comprobante</th>
                </tr>
              </thead>
              {visibleYears.map(([year, yearItems]) => (
                <tbody key={year}>
                  <tr className="member-self__year-row">
                    <th colSpan={6}>
                      <button
                        type="button"
                        className="member-self__year-toggle"
                        aria-expanded={expandedYear === year}
                        onClick={() =>
                          setExpandedYear((current) => (current === year ? null : year))
                        }
                      >
                        <span className="member-self__year-title">
                          <strong>{year}</strong>
                          <small>{yearItems.length} obligaciones</small>
                        </span>
                        <span className="member-self__year-summary">
                          <span className="member-self__year-stat member-self__year-stat--paid">
                            <CircleCheck aria-hidden="true" />
                            {
                              yearItems.filter((item) => item.collection_status === 'paid').length
                            }{' '}
                            pagadas
                          </span>
                          <span className="member-self__year-stat member-self__year-stat--pending">
                            <Clock3 aria-hidden="true" />
                            {
                              yearItems.filter((item) => item.collection_status !== 'paid').length
                            }{' '}
                            pendientes
                          </span>
                          <span className="member-self__year-stat member-self__year-stat--total">
                            <CircleDollarSign aria-hidden="true" />
                            {formatUsd(
                              yearItems.reduce((total, item) => total + Number(item.amount), 0),
                            )}
                          </span>
                        </span>
                      </button>
                    </th>
                  </tr>
                  {expandedYear === year &&
                    yearItems.map((item) => (
                      <tr key={item.id}>
                        <td>{item.slot_number ?? '—'}</td>
                        <td className="member-self__period">{formatPeriod(item.period)}</td>
                        <td>{item.concept_name}</td>
                        <td>
                          <StatusBadge
                            tone={
                              item.collection_status === 'pending_collection'
                                ? 'warning'
                                : 'success'
                            }
                          >
                            {item.collection_status === 'pending_collection'
                              ? 'Pendiente'
                              : 'Registrada'}
                          </StatusBadge>
                        </td>
                        <td>
                          <span className="member-self__amount-receipt">
                            {formatUsd(item.amount)}
                          </span>
                        </td>
                        <td>
                          {item.receipt_payment_id ? (
                            (() => {
                              const payment = data.account.payments.find(
                                (candidate) => candidate.id === item.receipt_payment_id,
                              );
                              return payment ? (
                                <button
                                  type="button"
                                  className="member-self__receipt-detail"
                                  onClick={() => setSelectedPayment(payment)}
                                >
                                  <Eye aria-hidden="true" />
                                  <strong>{payment.sourceReference}</strong>
                                  <span>Ver detalle</span>
                                </button>
                              ) : null;
                            })()
                          ) : (
                            <span aria-label="Sin comprobante">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              ))}
            </table>
            <footer className="member-self__pagination">
              <ResultsCounter
                from={obligationsByYear.length ? (page - 1) * yearsPerPage + 1 : 0}
                to={Math.min(page * yearsPerPage, obligationsByYear.length)}
                total={obligationsByYear.length}
                noun="años"
              />
              <Pagination
                page={page}
                pages={totalPages}
                onChange={(value) => {
                  setPage(value);
                  setExpandedYear(null);
                }}
              />
              <PageSizeSelector
                value={yearsPerPage}
                options={[1, 2, 3, 5]}
                onChange={(value) => {
                  setYearsPerPage(value);
                  setPage(1);
                  setExpandedYear(null);
                }}
                label="Años por página"
              />
            </footer>
          </div>
        ) : (
          <div className="member-self__empty">
            <ReceiptText />
            <strong>No tienes obligaciones registradas</strong>
            <p>
              Cuando existan conceptos aprobados, aparecerán aquí separados por período y alícuota.
            </p>
          </div>
        )}
      </section>
      <PaymentApplicationDetailModal
        payment={selectedPayment}
        onClose={() => {
          setSelectedPayment(null);
          if (window.location.hash.includes('?payment='))
            window.history.replaceState(null, '', '#mi-saldo');
        }}
        viewer="socio"
        evidenceUrl={(id) => memberSelfPaymentReceiptUrl(apiBase, id)}
        receiptUrl={(id) => memberSelfPaymentReceiptDownloadUrl(apiBase, id)}
      />
      <aside className="member-self__notice">
        <ShieldCheck />
        <div>
          <strong>Información personal protegida</strong>
          <p>Esta sesión solo permite consultar la cuenta vinculada a tu cédula.</p>
        </div>
      </aside>
    </div>
  );
}
